import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { auth, fmtAmt, toPyeong, lbl, sel, card } from '../RealTradePage';

// ─────────────────────────────────────────────
// 신규등록 탭 - /api/real-trades/new
//
//  국토부 API에는 "신고일"이 없다. 그래서 수집 스크립트가 처음 발견한 시각
//  (first_seen_at)을 기준으로 신규 여부를 판단한다.
//  → 야간 배치가 매일 돌기 시작하면 진짜 "어제 신고된 거래"만 잡힌다.
// ─────────────────────────────────────────────

export default function NewTrades({ lawdCd, regions, propType, zone}) {
  const [days, setDays]   = useState('1');
  const [scope, setScope] = useState('one');  // one=선택지역 / all=전체
  const [data, setData]   = useState({ rows: [], by_region: [], count: 0 });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams({ days, size: 300, prop_type: propType });
      if (scope === 'one') {
        if (lawdCd) q.set('lawd_cd', lawdCd);
        else if (zone) q.set('zone', zone);
      }
      const res = await axios.get(`/api/real-trades/new?${q}`, auth());
      setData(res.data);
    } catch (e) {
      alert('조회 실패: ' + (e.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  }, [days, scope, lawdCd, zone, propType]);

  useEffect(() => { load(); }, [load]);

  const sggName = zone || regions.find(r => r.lawd_cd === lawdCd)?.sgg_nm || '';

  return (
    <div>
      <div style={{ ...card, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <label style={lbl}>범위</label>
        <select value={scope} onChange={e => setScope(e.target.value)} style={sel}>
          <option value="one">선택 지역 ({sggName})</option>
          <option value="all">전체 지역</option>
        </select>

        <label style={lbl}>기간</label>
        <select value={days} onChange={e => setDays(e.target.value)} style={sel}>
          <option value="1">최근 1일</option>
          <option value="3">최근 3일</option>
          <option value="7">최근 7일</option>
          <option value="30">최근 30일</option>
        </select>

        <span style={{ fontSize: 13, color: '#475569', marginLeft: 8 }}>
          신규 <b style={{ color: '#dc2626' }}>{data.count}</b>건
          {loading && <span style={{ marginLeft: 8, color: '#3b82f6' }}>조회 중…</span>}
        </span>
      </div>

      {/* ── 지역별 요약 (전체 조회 시 유용) ── */}
      {data.by_region?.length > 1 && (
        <div style={{ ...card, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {data.by_region.map(r => (
            <span key={r.sgg_nm} style={{ padding: '4px 10px', fontSize: 12,
              background: '#f1f5f9', borderRadius: 12 }}>
              {r.sgg_nm} <b style={{ color: '#dc2626' }}>{r.cnt}</b>
            </span>
          ))}
        </div>
      )}

      <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f1f5f9' }}>
              {['수집시각','지역','법정동','단지명','면적','층','거래일','금액','평당가','유형']
                .map(h => <th key={h} style={th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r, i) => (
              <tr key={i} style={{ borderTop: '1px solid #f1f5f9' }}>
                <td style={{ ...td, color: '#94a3b8', fontSize: 11 }}>
                  {r.first_seen_at?.slice(5, 16)}
                </td>
                <td style={td}>{r.sgg_nm}</td>
                <td style={td}>{r.umd_nm}</td>
                <td style={{ ...td, fontWeight: 600 }}>{r.apt_nm}</td>
                <td style={tdR}>{toPyeong(r.exclu_use_ar)}평</td>
                <td style={tdR}>{r.floor_no}층</td>
                <td style={td}>{r.deal_date}</td>
                <td style={{ ...tdR, fontWeight: 600, color: '#1e40af' }}>
                  {fmtAmt(r.deal_amount)}
                </td>
                <td style={tdR}>{r.unit_price?.toLocaleString()}</td>
                <td style={td}>{r.dealing_gbn || '-'}</td>
              </tr>
            ))}
            {data.rows.length === 0 && !loading && (
              <tr><td colSpan={10} style={{ ...td, textAlign: 'center',
                padding: 30, color: '#94a3b8' }}>
                신규 등록된 실거래가 없습니다
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <p style={{ marginTop: 10, fontSize: 11, color: '#94a3b8' }}>
        ※ "수집시각"은 우리 시스템이 해당 거래를 처음 발견한 시점입니다.
        국토부가 신고일을 제공하지 않아 이 방식으로 신규 여부를 판단합니다.
      </p>
    </div>
  );
}

const th  = { padding: '8px', textAlign: 'left', fontWeight: 600,
              color: '#475569', whiteSpace: 'nowrap' };
const td  = { padding: '8px', whiteSpace: 'nowrap' };
const tdR = { ...td, textAlign: 'right' };